/**
 * Platform console shell and routes.
 *
 * Mounted only when the tenancy app resolved `mode: "platform"` for this host, so there
 * are no learner or tenant-admin routes here at all — not hidden ones, absent ones.
 *
 * The layout is `shared/layout/WorkspaceShell`, the same sidebar + top bar the tenant
 * admin uses. It was a bar-only header until the console grew a second area, and one
 * horizontal row is exactly as far as that goes: a console with two menu groups today and
 * platform defaults, flags and audit still to come needs somewhere for them to go that
 * does not hide where you are behind a dropdown. Sharing the component with the admin also
 * means the two surfaces cannot drift apart in collapse behaviour or active state, which
 * is what happened while they were separate.
 *
 * Nav groups are declared once, below. Adding a surface is one entry, and the nav never
 * advertises a page that does not exist — which is why there is no group here for platform
 * defaults or audit yet.
 */

import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import type { Session } from '../shared/lib/session';
import { signOut } from '../shared/lib/session';
import { Icon } from '../shared/ui';
import { WorkspaceShell, type WorkspaceNavGroup } from '../shared/layout/WorkspaceShell';
import TenantList from './routes/TenantList';
import TenantCreate from './routes/TenantCreate';
import TenantDetail from './routes/TenantDetail';
import CourseList from './routes/CourseList';
import Login from './routes/Login';
import './console.css';

const NAV: WorkspaceNavGroup[] = [
  {
    heading: 'Academies',
    items: [{ to: '/tenants', label: 'Tenants', icon: 'apartment' }],
  },
  {
    heading: 'Catalogue',
    items: [{ to: '/courses', label: 'Courses', icon: 'book_2' }],
  },
];

export function SuperadminApp({ session }: { session: Session }) {
  // Two distinct failures with different remedies: not signed in, and signed in
  // without authority. Collapsing them into one screen sends people to reset a
  // password that was never the problem.
  if (!session.authenticated) return <Login />;
  if (!session.permissions.isSuperuser) return <Login refused />;

  const router = createBrowserRouter([
    {
      element: <ConsoleShell session={session} />,
      children: [
        { index: true, element: <Navigate to="/tenants" replace /> },
        { path: 'tenants', element: <TenantList /> },
        { path: 'tenants/new', element: <TenantCreate /> },
        { path: 'tenants/:slug', element: <TenantDetail /> },
        { path: 'courses', element: <CourseList /> },
        { path: '*', element: <Navigate to="/tenants" replace /> },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}

function ConsoleShell({ session }: { session: Session }) {
  return (
    <WorkspaceShell
      storageKey="thinlms.console.sidebar"
      // Fixed, and derived from no tenant record — there is no academy to name here. The
      // mark is an icon rather than an initial for the same reason: an initial would be a
      // brand's, and this surface belongs to all of them.
      brand={{ mark: <Icon name="hub" />, title: 'Platform', subtitle: 'Console', home: '/tenants' }}
      groups={NAV}
      // Naming the authority in the chrome, permanently. Every action taken here affects
      // every academy, and that should never be a surprise.
      scope={{ icon: 'public', label: 'All tenants' }}
      actions={
        <>
          <span className="workspace__user">{session.user?.username}</span>
          <button type="button" className="ui-btn" data-variant="ghost" onClick={signOut}>
            Log out
          </button>
        </>
      }
    />
  );
}
